import { ResizeController } from "@lit-labs/observers/resize-controller.js";
import classNames from "classnames";
import { html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";

import { HighlightController } from "../../utils/highlight";
import { LookupController } from "../../utils/lookup";
import { ScrollController } from "../../utils/scroll";
import { styles } from "../../utils/zoomable-scrollable-slide.styles";

import componentStyles from "./scrollable-slide.css";

import type { SlideBackgroundColor, SlideState } from "../../types.d";
import type { SlideViewMode } from "../slide/slide";
import type { PropertyValueMap } from "lit";
import type { Ref } from "lit/directives/ref.js";

const CONTENT_VERTICAL_PADDING = 40;

const DEFAULT_SLIDE_HEIGHT = 540;
const DESKTOP_MAX_TITLE_HEIGHT = 106;
const DESKTOP_MIN_TITLE_HEIGHT = 88;
const MOBILE_MAX_TITLE_HEIGHT = 116;
const MOBILE_MIN_TITLE_HEIGHT = 98;

@customElement("bp-scrollable-slide")
export class ScrollableSlide extends LitElement {
  static styles = [styles, componentStyles];

  @property({ attribute: "background-color" })
  backgroundColor?: SlideBackgroundColor = "white";

  contentRef: Ref<HTMLElement> = createRef();

  @property({
    type: Boolean,
    attribute: "hide-shadow",
  })
  hideShadow = false;

  highlightController = new HighlightController();

  windowRef: Ref<HTMLElement> = createRef();

  lookupController = new LookupController({
    windowRef: this.windowRef,
  });
  slideRef: Ref<HTMLElement> = createRef();
  scrollController = new ScrollController({
    slideRef: this.slideRef,
    verticalPadding: CONTENT_VERTICAL_PADDING,
    minWindowHeight: this.minContentWindowHeight,
    maxWindowHeight: this.maxContentWindowHeight,
  });

  slideHeight = DEFAULT_SLIDE_HEIGHT;

  @property()
  slideState?: SlideState;

  @property({ attribute: "view-mode" })
  viewMode?: SlideViewMode = "default";

  get maxContentWindowHeight() {
    return this.slideHeight - this.minTitleHeight;
  }

  get maxTitleHeight() {
    return this.viewMode === "default"
      ? DESKTOP_MAX_TITLE_HEIGHT
      : MOBILE_MAX_TITLE_HEIGHT;
  }

  get minContentWindowHeight() {
    return this.slideHeight - this.maxTitleHeight;
  }
  get minTitleHeight() {
    return this.viewMode === "default"
      ? DESKTOP_MIN_TITLE_HEIGHT
      : MOBILE_MIN_TITLE_HEIGHT;
  }
  private slideObserver = new ResizeController(this, {
    callback: (entries) => {
      entries
        .filter((entry) => entry.target.className.includes("scrollable-slide"))
        .forEach((entry) => {
          this.slideHeight = entry.contentRect.height;
          this.scrollController.minWindowHeight = this.minContentWindowHeight;
          this.scrollController.maxWindowHeight = this.maxContentWindowHeight;
          this.updateState();
        });
    },
  });

  firstUpdated() {
    if (this.slideRef?.value) {
      this.slideObserver.observe(this.slideRef.value);
    }
  }

  handleContentUpdate(event: Event) {
    const contentEl = (event.target as HTMLSlotElement)?.assignedNodes({
      flatten: true,
    })[0] as HTMLElement;
    this.highlightController.contentEl = contentEl;
    this.lookupController.contentEl = contentEl;
    this.scrollController.contentEl = contentEl;
    this.updateState();
  }

  render() {
    // This will be overridden by scroll controller.
    const initStyle = "--scroll-transition: 0; --title-transition: 0;";
    const overflowShadowStyles = classNames("overflow-shadow", {
      "overflow-shadow-hidden": this.hideShadow,
    });

    return html`
      <div
        ${ref(this.slideRef)}
        class="scrollable-slide ${this.viewMode} background-${this
          .backgroundColor}"
        style="${initStyle}"
      >
        <slot name="title"></slot>
        <div class="content-window" ${ref(this.windowRef)}>
          <div class="transformed-content" ${ref(this.contentRef)}>
            <div class="content">
              <slot
                name="content"
                @slotchange=${this.handleContentUpdate}
              ></slot>
            </div>
          </div>
        </div>
        <div class="${overflowShadowStyles}"></div>
      </div>
    `;
  }

  updateState() {
    this.highlightController.update(this.slideState?.highlightState || {});
    this.lookupController.update(this.slideState?.lookupState || {});
    this.scrollController.update(this.slideState?.transformState || {});
  }

  protected willUpdate(
    changedProperties: Map<PropertyKey, unknown> | PropertyValueMap<this>,
  ): void {
    if (changedProperties.has("slideState")) {
      this.updateState();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-scrollable-slide": ScrollableSlide;
  }
}
